
import { Grid, Link as MuiLink, useMediaQuery } from "@mui/material";
import { OrganizerCardContainer, OrganizerCardDetail } from "../styles";
import CustomButton from "../CustomButton";
 
import {
  convertToCents,
  stringToDecimal
} from "../../../../Utils/formatters";
 
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventListener } from "../../../../Hooks/useEventListener";
import { ScreenGreeting } from "../../../../Components/Molecules";
import { useTransactionsData } from "../../../../Hooks";
import { useFilter } from "../../../../Hooks/useFilter";
import { Container } from "../../Receipts/styles";
import { useToast } from "../../../../Hooks/toast";
import {
  useSubAccountCreateQRCode,
  useSubAccountGetQRCode,
} from "../../../../Hooks/useSubAccounts";
import { ArrowLeft, QrCode, Edit as EditIcon, Home as HomeIcon, DataSaverOff as DataSaverOffIcon, AttachMoney as MoneyIcon, Code as CodeIcon, Group as GroupIcon } from "@mui/icons-material";
import api from "../../../../Services/api";
import { Link, useParams } from "react-router-dom";
import { OrganizerForm, OrginizerFormRef } from "../OrganizerForm";
import ResumoSection from "./sections/ResumoSection";
import MensalidadeSection from "./sections/MensalidadeSection";
import ExtratoSection from "./sections/ExtratoSection";
import QRCodeSection from "./sections/QRCodeSection";
import APISection from "./sections/APISection";
import OperadoresSection from "./sections/OperadoresSection";
import styled from "styled-components";

interface SubAccountDetail {
  accountId: string
  available: number
  blocked: number
  isActive: boolean
  monthlyFee: number
  name: string
  subAccountId: string
}

type MenuKey = "Resumo" | "Mensalidade" | "Extrato" | "QRcode" | "API" | "Operadores";

interface MenuItem {
  key: MenuKey;
  label: string;
  icon: JSX.Element;
}

// Override the Receipts Container to ensure full-width layout here
const FullWidthContainer = styled(Container)`
  max-width: 100% !important;
`;

const DetailsOrganizer = () => {
  const [subAccount, setSubAccount] = useState<SubAccountDetail>({
    accountId: '',
    available: 0,
    blocked: 0,
    isActive: false,
    monthlyFee: 0,
    name: '',
    subAccountId: ''
  });

  const { subAccountId } = useParams<{ subAccountId: string }>();

  const matches = useMediaQuery("(min-width: 1220px)");
  const [menuSelected, handleMenuSelected] = useState<MenuKey>("Resumo");
  const [amount, setAmount] = useState<string>("");
  const [qrCode, setQrCode] = useState<string>("");

  const [operators, setOperators] = useState<any[]>([]);

  const getOperators = useCallback(async () => {
    if (!subAccountId) return;
    try {
      const { data } = await api.get(`operators?subAccountId=${subAccountId}`);
      setOperators(data);
    } catch (error) {
      console.error("Error fetching operators", error);
    }
  }, [subAccountId]);

  const getById = useCallback(async () => {
    if (!subAccountId) return;
    try {
      const res = await api.get(`subaccount/${subAccountId}`);
      setSubAccount(res.data);
    } catch (error) {
      console.error("Error fetching subaccount by id", error);
    }
  }, [subAccountId]);

  useEffect(() => {
    getById();
    getOperators();
  }, [subAccountId, getById, getOperators])

  // Refresh subaccount info (including available) when global refresh-balance is dispatched
  useEventListener("refresh-balance", getById, window as any);


  const { getFilterObj, handleApplyFilters } = useFilter();
  const extractFilterObj = getFilterObj("extract");

  const { addToast } = useToast();

  const [page, setPage] = useState(1);

  const {
    handleGET: fetchSubAccountDetails,
    loading,
    state: subAccountData,
  } = useTransactionsData();

  const {
    handleGET: fetchSubAccountQRCode,
    loading: _0,
    state: subAccountQRCode,
  } = useSubAccountGetQRCode();

  const {
    handlePOST: postSubAccountQRCode,
    loading: loadingCreateQRCode,
    state: subAccountQRCodeResponse,
    success: successQRCode,
  } = useSubAccountCreateQRCode();

  const handleSetToFirstPage = useCallback(() => {
    setPage(1);
  }, []);

  const handleChangePage = useCallback((event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handleCopy = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      addToast({
        title: "Successo",
        description: "Chave copiada com sucesso.",
        type: "success",
      });
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  }, [addToast]);

  useEffect(() => {
    if (subAccountQRCodeResponse?.data) {
      addToast({
        title: "Successo",
        description: "QRCode criado com sucesso.",
        type: "success",
      });
      setQrCode(subAccountQRCodeResponse?.data.qrcode);
      setAmount(subAccountQRCodeResponse?.data.amount);
    }
  }, [subAccountQRCodeResponse?.data]);

  useEffect(() => {
    if (menuSelected === "QRcode") {
      fetchSubAccountQRCode({ id: subAccountId });
    }
  }, [subAccountId, menuSelected]);

  // Ensure extract filter includes current subAccountId (only when missing/different)
  useEffect(() => {
    if (!subAccountId) return;
    const current: any = getFilterObj("extract");
    const needsUpdate =
      current?.subAccountId !== subAccountId ||
      current?.sub_account_id !== subAccountId;

    if (needsUpdate) {
      handleApplyFilters("extract", {
        subAccountId,
        sub_account_id: subAccountId,
      });
    }
  }, [subAccountId]);

  useEffect(() => {
    const queryParameters = {
      is_paged_result: true,
      page: page,
      page_size: 15,
      type: "all",
      subAccountId: subAccountId,
      sub_account_id: subAccountId,
      ...extractFilterObj,
    };

    fetchSubAccountDetails({ ...queryParameters });
  }, [extractFilterObj, page, subAccountId]);

  useEffect(() => {
    if (subAccountQRCode) {
      setQrCode(subAccountQRCode.qrcode);
      setAmount(subAccountQRCode.amount);
    }
  }, [subAccountQRCode]);

  if (!subAccountData) return null;


  const handleSubmitValue = useCallback(async (formData: any) => {
    const cents = convertToCents(formData.value);
    const valueAsDecimal = stringToDecimal(formData.value);

    try {
      await api.post(`/SubAccount/api/UpdateMonthlyFee/${subAccountId}/${cents}`, {});
      addToast({ description: "", title: "mensalidade atualizada", type: "success" });
      setSubAccount((prev) => ({ ...prev, monthlyFee: cents }));

      const eventRefreshBalance = new Event("refresh-balance");
      window.dispatchEvent(eventRefreshBalance);
    } catch (err: any) {
      addToast({
        description: err?.response?.data?.description ?? "Não foi possível atualizar a mensalidade.",
        title: err?.response?.data?.information ?? "Erro",
        type: "error",
      });
    }
  }, [addToast, subAccountId]);

  const organizerFormRef = useRef<OrginizerFormRef>(null);

  const MENU_ITEMS: MenuItem[] = useMemo(
    () => [
      { key: "Resumo", label: "Resumo", icon: <HomeIcon /> },
      { key: "Mensalidade", label: "Mensalidade", icon: <MoneyIcon /> },
      { key: "Extrato", label: "Extrato", icon: <DataSaverOffIcon /> },
      { key: "QRcode", label: "QRCode", icon: <QrCode /> },
      { key: "API", label: "API", icon: <CodeIcon /> },
      { key: "Operadores", label: "Operadores", icon: <GroupIcon /> },
    ],
    []
  );

  const handleMenuClick = useCallback((key: MenuKey) => {
    handleMenuSelected(key);
  }, []);

  return (
    <FullWidthContainer style={{ marginLeft: 0 }}>
      <ScreenGreeting
        title={
          <>
            Ponto de venda: <MuiLink underline="hover" onClick={() => {
              organizerFormRef.current.openOrganizer({ 
                name: subAccount.name,
                isActive: subAccount.isActive,
                subAccountId: subAccount.subAccountId
              })
            }}>
              {subAccount.name} <EditIcon/> 
            </MuiLink>
          </>
        }
        subTitle="Detalhes do ponto de venda"
      />
      <OrganizerForm 
        onSucess={() => getById()} 
        ref={organizerFormRef}
      />
      <Grid container spacing={0}>
        <Grid item xs={12} md={3}>
          <OrganizerCardContainer>
            <Grid container marginBottom={4}>
              <Link to={'.'}>
              <CustomButton startIcon={<ArrowLeft />} onClick={() => { }}>
                Voltar
              </CustomButton>
              </Link>
            </Grid>
            <Grid
              container
              xs={12}
              spacing={2}
              flexDirection="row"
              marginRight={0}
            >
              {MENU_ITEMS.map((item) => (
                <Grid item xs={12} key={item.key}>
                  <CustomButton
                    onClick={() => handleMenuClick(item.key)}
                    fullWidth
                    variant={menuSelected === item.key ? "contained" : "outlined"}
                    startIcon={item.icon}
                  >
                    {item.label}
                  </CustomButton>
                </Grid>
              ))}
            </Grid>
          </OrganizerCardContainer>
        </Grid>
        <Grid item xs={12} md={9}>
          {menuSelected === "Resumo" && (
            <OrganizerCardDetail>
              <ResumoSection name={subAccount.name} available={subAccount.available} subAccountId={subAccount.subAccountId} />
            </OrganizerCardDetail>
          )}
          {menuSelected === "Mensalidade" && (
            <OrganizerCardDetail>
              <MensalidadeSection name={subAccount.name} monthlyFee={subAccount.monthlyFee} onSubmit={handleSubmitValue} />
            </OrganizerCardDetail>
          )}
          {menuSelected === "Extrato" && (
            <OrganizerCardDetail>
              <ExtratoSection matches={matches} subAccountData={subAccountData} loading={loading} handleSetToFirstPage={handleSetToFirstPage} handleChangePage={handleChangePage} page={page} />
            </OrganizerCardDetail>
          )}
          {menuSelected === "QRcode" && (
            <OrganizerCardDetail>
              <QRCodeSection qrCode={qrCode} amount={amount} onCopy={handleCopy} setAmount={setAmount} onCreate={postSubAccountQRCode} subAccountId={subAccount.subAccountId} />
            </OrganizerCardDetail>
          )}
          {menuSelected === "API" && (
            <OrganizerCardDetail>
              <APISection handleCopy={handleCopy} subAccountId={subAccount.subAccountId} />
            </OrganizerCardDetail>
          )}
          {menuSelected === "Operadores" && (
            <OrganizerCardDetail>
              <OperadoresSection operators={operators} subAccountId={subAccount.subAccountId} onChanged={() => getOperators()} />
            </OrganizerCardDetail>
          )}
        </Grid>
      </Grid>
    </FullWidthContainer>

  );
};
export default DetailsOrganizer;
